"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { ArrowUpRight } from "lucide-react";
import { type CSSProperties, useCallback, useMemo, useRef } from "react";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import { z } from "zod";

const FeeMetaSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  meta_priority: z.coerce.number().optional().default(0),
  meta_eligibility_orange: z.string().optional().default(""),
  meta_total_fee_label: z.string().optional().default("Total Fee"),
  meta_total_fee_amount: z.string().optional().default(""),
  meta_block_label: z.string().optional().default(""),
  meta_block_amount: z.string().optional().default(""),
  meta_emi_label: z.string().optional().default(""),
  meta_emi_amount: z.string().optional().default(""),
  meta_footnote_1: z.string().optional().default(""),
  meta_footnote_1_color: z.string().optional().default("#6B6B6B"),
  meta_footnote_2: z.string().optional().default(""),
  meta_footnote_2_color: z.string().optional().default("#6B6B6B"),
  meta_prerequisite_label: z.string().optional().default("Prerequisite:"),
  meta_prerequisite_label_color: z.string().optional().default("#D32F2F"),
  meta_prerequisite_text: z.string().optional().default(""),
  meta_prerequisite_text_color: z.string().optional().default("#2C2C2C"),
  meta_cta_url: z.string().optional().default(""),
  meta_total_fee_box_bg: z.string().optional().default("#1A1A4B"),
  meta_secondary_box_bg: z.string().optional().default("#E8EAF6"),
  meta_secondary_border: z.string().optional().default("#1A1A4B"),
  meta_navy_text: z.string().optional().default("#1A1A4B"),
  meta_accent_orange: z.string().optional().default("#E67E22"),
  meta_prerequisite_box_bg: z.string().optional().default("#F3F4F8"),
  meta_prerequisite_box_border: z.string().optional().default("#D7DAE5"),
  meta_subtitle_color: z.string().optional().default("#4A4A4A"),
});

const SectionSchema = z
  .object({
    title: z.string().optional().default(""),
    title_start_color: z.string().optional().default("#171719"),
    pre_title: z.string().optional().default(""),
    pre_title_color: z.string().optional().default("#E67E22"),
    subtitle: z.string().optional().default(""),
    desktop_background_color: z.string().optional().default("#FFFFFF"),
    button_text: z.string().optional().default("Apply Now"),
    button_color: z.string().optional().default("#E67E22"),
    button_text_color: z.string().optional().default("#FFFFFF"),
    button_is_cta: z.union([z.boolean(), z.number()]).optional(),
    metadata: z.array(FeeMetaSchema).optional().default([]),
    status: z.union([z.boolean(), z.number()]).optional(),
  })
  .transform((d) => ({
    ...d,
    metadata: [...(d.metadata || [])].sort(
      (a, b) => (a.meta_priority || 0) - (b.meta_priority || 0),
    ),
  }));

type FeeMeta = z.infer<typeof FeeMetaSchema>;

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

function pickFeeRow(metadata: FeeMeta[]): FeeMeta {
  const row = metadata[0];
  const parsed = FeeMetaSchema.safeParse(row ?? {});
  return parsed.success ? parsed.data : FeeMetaSchema.parse({});
}

export default function ProgramBenefitsCourseFee({ data }: { data: any }) {
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const raw = data?.data ?? data ?? {};
  const parsed = useMemo(() => SectionSchema.safeParse(raw), [raw]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "program_benefits_course_fee_4");
  //   }
  // }, []);

  if (!parsed.success) {
    return null;
  }

  const section = parsed.data;
  const isOff =
    section.status === false ||
    section.status === 0 ||
    String(section.status) === "0";

  if (isOff) {
    return null;
  }

  const fee = pickFeeRow(section.metadata ?? []);
  const subtitleColor = fee.meta_subtitle_color?.trim() || "#4A4A4A";
  const accent = fee.meta_accent_orange?.trim() || "#E67E22";
  const navy = fee.meta_navy_text?.trim() || "#1A1A4B";
  const totalBg = fee.meta_total_fee_box_bg?.trim() || "#1A1A4B";
  const lav = fee.meta_secondary_box_bg?.trim() || "#E8EAF6";
  const border = fee.meta_secondary_border?.trim() || "#1A1A4B";
  const preBg = fee.meta_prerequisite_box_bg?.trim() || "#F3F4F8";
  const preBorder = fee.meta_prerequisite_box_border?.trim() || "#D7DAE5";

  const ctaHrefRaw = (fee.meta_cta_url || "").trim();
  const ctaHrefNormalized = ctaHrefRaw ? normalizeHref(ctaHrefRaw) : "";

  const buttonIsCta = isCmsShowFlagOn(section.button_is_cta, true);
  const opensLeadPopup = buttonIsCta || !ctaHrefNormalized;

  const handleApplyClick = useCallback(() => {
    ctaClick({
      section: "program_benefits_course_fee_4",
      component: "cta_apply",
      label: section.button_text?.trim() || "apply",
      step: "1",
      entity: "program_benefits_4",
      parentEntity: "program_benefits",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
      }),
    );
  }, [dispatch, section.button_text]);

  const hasHeader =
    Boolean(section.title?.trim()) ||
    Boolean(section.pre_title?.trim()) ||
    Boolean(section.subtitle?.trim());

  const hasFeeBlock =
    Boolean(fee.meta_total_fee_amount?.trim()) ||
    Boolean(fee.meta_block_amount?.trim()) ||
    Boolean(fee.meta_emi_amount?.trim());

  const hasPrereq =
    Boolean(fee.meta_prerequisite_label?.trim()) ||
    Boolean(fee.meta_prerequisite_text?.trim());

  if (!hasHeader && !hasFeeBlock && !hasPrereq) {
    return null;
  }

  const btnStyle: CSSProperties = {
    backgroundColor: section.button_color || "#E67E22",
    color: section.button_text_color || "#FFFFFF",
  };

  const sideBoxStyle: CSSProperties = {
    backgroundColor: lav,
    borderColor: border,
    color: navy,
  };

  const totalBoxStyle: CSSProperties = totalBg.toLowerCase().includes("gradient")
    ? { background: totalBg }
    : { backgroundColor: totalBg };

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative w-full flex flex-col items-center",
        "py-8 px-6 min-[640px]:py-12 min-[640px]:px-8 min-[1024px]:py-[72px] min-[1024px]:px-[99px]",
      )}
      style={{
        backgroundColor: section.desktop_background_color || "#FFFFFF",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div
        className={cn(
          "relative z-[1] flex w-full max-w-[720px] flex-col items-center gap-4 text-center",
          "min-[640px]:gap-5",
        )}
      >
        {(section.title || section.pre_title) && (
          <h2
            className={cn(
              "text-center",
              "text-[22px] font-semibold leading-snug min-[640px]:text-[28px] min-[1024px]:text-[32px]",
            )}
          >
            {section.title ? (
              <span style={{ color: section.title_start_color || "#171719" }}>
                {section.title}
              </span>
            ) : null}
            {section.title?.trim() && section.pre_title?.trim() ? " " : null}
            {section.pre_title ? (
              <span style={{ color: section.pre_title_color || "#E67E22" }}>
                {section.pre_title}
              </span>
            ) : null}
          </h2>
        )}

        {section.subtitle ? (
          <p
            className="max-w-[640px] text-[15px] font-normal leading-relaxed min-[640px]:text-[16px]"
            style={{
              color: subtitleColor,
            }}
          >
            {section.subtitle}
          </p>
        ) : null}

        {section.button_text ? (
          <div className="flex w-full justify-center pt-1">
            {opensLeadPopup ? (
              <button
                type="button"
                onClick={handleApplyClick}
                className={cn(
                  "inline-flex items-center gap-2 rounded-[10px] px-6 py-3",
                  "text-[15px] font-semibold shadow-sm transition-opacity hover:opacity-95",
                  "min-[640px]:px-7 min-[640px]:py-3.5 min-[640px]:text-base",
                )}
                style={btnStyle}
              >
                {section.button_text}
                <ArrowUpRight className="size-5 shrink-0 min-[640px]:size-[22px]" aria-hidden />
              </button>
            ) : (
              <a
                href={ctaHrefNormalized}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "inline-flex items-center gap-2 rounded-[10px] px-6 py-3",
                  "text-[15px] font-semibold shadow-sm transition-opacity hover:opacity-95",
                  "min-[640px]:px-7 min-[640px]:py-3.5 min-[640px]:text-base",
                )}
                style={btnStyle}
                onClick={() =>
                  ctaClick({
                    section: "program_benefits_course_fee_4",
                    component: "cta_apply_link",
                    label: section.button_text?.trim() || "apply",
                    step: "1",
                    entity: "program_benefits_4",
                    parentEntity: "program_benefits",
                  })
                }
              >
                {section.button_text}
                <ArrowUpRight className="size-5 shrink-0 min-[640px]:size-[22px]" aria-hidden />
              </a>
            )}
          </div>
        ) : null}

        {fee.meta_eligibility_orange?.trim() ? (
          <p
            className="w-full max-w-[520px] self-center pt-2 text-left text-[18px] font-medium leading-relaxed min-[640px]:max-w-[640px] min-[1024px]:max-w-[663px]"
            style={{
              color: accent,
            }}
          >
            {fee.meta_eligibility_orange}
          </p>
        ) : null}
      </div>

      {hasFeeBlock && (
        <div
          className={cn(
            "relative z-[1] mt-8 flex w-full max-w-[520px] flex-col gap-3 min-[640px]:mt-10",
            "min-[1024px]:max-w-[663px]",
          )}
        >
          <div
            className={cn(
              "flex w-full flex-col gap-3",
              "min-[1024px]:flex-row min-[1024px]:items-stretch min-[1024px]:justify-center min-[1024px]:gap-4",
            )}
          >
            <div
              className={cn(
                "order-2 flex h-[347px] flex-col items-center justify-center px-6 py-5 text-center",
                "min-[1024px]:order-1 min-[1024px]:h-[283px] min-[1024px]:w-[391px] min-[1024px]:shrink-0 min-[1024px]:items-start min-[1024px]:text-left",
              )}
              style={totalBoxStyle}
            >
              <p className="text-[21px] font-normal italic text-white/90 min-[1024px]:text-[16px]">
                {fee.meta_total_fee_label || "Total Fee"}
              </p>
              <p className="mt-2 text-[37px] font-bold leading-none text-white min-[1024px]:text-[39px]">
                {fee.meta_total_fee_amount || "—"}
              </p>
            </div>

            <div className="order-1 flex flex-col gap-3 min-[1024px]:order-2 min-[1024px]:w-[256px] min-[1024px]:shrink-0 min-[1024px]:gap-[11px]">
              <div
                className="flex flex-col items-center justify-center border p-4 text-center min-[1024px]:h-[136px] min-[1024px]:items-start min-[1024px]:px-6 min-[1024px]:py-5 min-[1024px]:text-left"
                style={sideBoxStyle}
              >
                <p className="text-[16px] font-medium leading-snug">
                  {fee.meta_block_label || ""}
                </p>
                <p className="mt-2 text-[28px] font-bold leading-none">
                  {fee.meta_block_amount || "—"}
                </p>
              </div>
              <div
                className="flex flex-col items-center justify-center border p-4 text-center min-[1024px]:h-[136px] min-[1024px]:items-start min-[1024px]:px-6 min-[1024px]:py-5 min-[1024px]:text-left"
                style={sideBoxStyle}
              >
                <p className="text-[16px] font-medium leading-snug">
                  {fee.meta_emi_label || ""}
                </p>
                <p className="mt-2 text-[28px] font-bold leading-none">
                  {fee.meta_emi_amount || "—"}
                </p>
              </div>
            </div>
          </div>

          {(fee.meta_footnote_1?.trim() || fee.meta_footnote_2?.trim()) && (
            <div className="mt-2 w-full space-y-1 text-left text-[14px] leading-relaxed">
              {fee.meta_footnote_1?.trim() ? (
                <p style={{ color: fee.meta_footnote_1_color?.trim() || "#6B6B6B" }}>
                  {fee.meta_footnote_1}
                </p>
              ) : null}
              {fee.meta_footnote_2?.trim() ? (
                <p style={{ color: fee.meta_footnote_2_color?.trim() || "#6B6B6B" }}>
                  {fee.meta_footnote_2}
                </p>
              ) : null}
            </div>
          )}
        </div>
      )}

      {hasPrereq && (
        <div
          className={cn(
            "relative z-[1] mt-8 w-full max-w-[520px] rounded-[20px] border px-5 py-4 text-left min-[640px]:mt-10",
            "min-[640px]:max-w-[640px] min-[640px]:px-6 min-[640px]:py-5",
          )}
          style={{
            backgroundColor: preBg,
            borderColor: preBorder,
          }}
        >
          <p
            className="text-[14px] font-bold leading-snug min-[640px]:text-[15px]"
            style={{
              color: fee.meta_prerequisite_label_color || "#D32F2F",
            }}
          >
            {fee.meta_prerequisite_label || "Prerequisite:"}
          </p>
          <p
            className="mt-2 text-[16px] font-semibold leading-relaxed"
            style={{
              color: fee.meta_prerequisite_text_color?.trim() || "#2C2C2C",
            }}
          >
            {fee.meta_prerequisite_text || ""}
          </p>
        </div>
      )}
    </section>
  );
}
